import React from 'react';
import {Link, useLocation} from 'react-router';
import {useTranslation} from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ErrorPage from '../ErrorPage/ErrorPage';
import {DEFAULT_LANGUAGE, localePath} from '../../languages';
import config from '../../config';
import BlogCard, {ArticleMeta, ArticleDate} from '../../components/BlogCard/BlogCard';
import ShareButton from './ShareButton';
import RelatedArticles from './RelatedArticles';
import {SocialMeta} from '../../components/PageMeta/PageMeta';
import {socialImage} from '../../social';
import BrandMark from '../../components/BrandMark';

const absolute = (path) => new URL(path, config.homeUrl).href;
const jsonLd = (value) => JSON.stringify(value).replace(/</g, '\\u003c');

export default function Blog({content}) {
    const {pathname} = useLocation();
    const {t, i18n} = useTranslation();
    if (!content || content.pathname !== pathname) return <ErrorPage />;
    const article = content.article;
    const lang = i18n.language;
    const pageLabel = content.page > 1 ? ` · ${t('blog.page', {page: content.page})}` : '';
    const title = article
        ? `${article.title} | YourLovely.Place`
        : `${t('footer.blog')}${pageLabel} | YourLovely.Place`;
    const description = article ? article.description : t('blog.description');
    const canonicalPath = pathname + (!article && content.page > 1 ? `?page=${content.page}` : '');
    const canonical = absolute(canonicalPath);
    const alternates = article ? [] : content.translations || [];
    const defaultAlternate = alternates.find((a) => a.lang === DEFAULT_LANGUAGE);
    const schema = article
        ? {
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: article.title,
              description: article.description,
              image: article.cover?.url || socialImage.url,
              '@id': `${canonical}#article`,
              isAccessibleForFree: true,
              isPartOf: {
                  '@type': 'Blog',
                  '@id': absolute(localePath('/blog', lang)) + '#blog',
                  url: absolute(localePath('/blog', lang)),
                  name: `${config.title} · ${t('footer.blog')}`,
              },
              inLanguage: lang,
              datePublished: article.publishedAt,
              dateModified: article.updatedAt > article.publishedAt ? article.updatedAt : article.publishedAt,
              timeRequired: `PT${article.readingMinutes}M`,
              url: canonical,
              mainEntityOfPage: {'@type': 'WebPage', '@id': canonical},
              publisher: {
                  '@type': 'Organization',
                  '@id': absolute('/#organization'),
                  name: config.title,
                  url: config.homeUrl,
                  logo: {'@type': 'ImageObject', url: absolute('/public/favicons/apple-touch-icon.png')},
              },
              author: {'@type': 'Organization', name: config.title, url: absolute('/about')},
          }
        : {
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: title,
              description,
              url: canonical,
              inLanguage: lang,
              mainEntity: {
                  '@type': 'ItemList',
                  itemListElement: content.articles.map((item, index) => ({
                      '@type': 'ListItem',
                      position: index + 1,
                      url: absolute(item.path),
                      name: item.title,
                  })),
              },
          };
    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            {alternates.map((item) => (
                <link key={item.lang} rel="alternate" hrefLang={item.lang} href={absolute(item.path)} />
            ))}
            {defaultAlternate && <link rel="alternate" hrefLang="x-default" href={absolute(defaultAlternate.path)} />}
            <link
                rel="alternate"
                type="application/rss+xml"
                title={t('footer.blog')}
                href={localePath('/blog/feed.xml', lang)}
            />
            <SocialMeta
                title={title}
                description={description}
                canonical={canonical}
                type={article ? 'article' : 'website'}
                image={article?.cover || socialImage}
                alternateLanguages={alternates.map(({lang}) => lang)}
            />
            <meta
                name="robots"
                content={
                    !article && content.articles.length === 0
                        ? 'noindex, follow'
                        : 'index, follow, max-image-preview:large'
                }
            />
            {article && (
                <>
                    <meta name="author" content={config.title} />
                    <meta property="article:published_time" content={article.publishedAt} />
                    <meta
                        property="article:modified_time"
                        content={article.updatedAt > article.publishedAt ? article.updatedAt : article.publishedAt}
                    />
                    <meta property="article:author" content={absolute('/about')} />
                </>
            )}
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: jsonLd(schema)}} />
            {article && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: jsonLd({
                            '@context': 'https://schema.org',
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                {
                                    '@type': 'ListItem',
                                    position: 1,
                                    name: t('header.home'),
                                    item: absolute(localePath('/', lang)),
                                },
                                {
                                    '@type': 'ListItem',
                                    position: 2,
                                    name: t('footer.blog'),
                                    item: absolute(localePath('/blog', lang)),
                                },
                                {'@type': 'ListItem', position: 3, name: article.title, item: canonical},
                            ],
                        }),
                    }}
                />
            )}
            <Header />
            <main className="blog-page design-container">
                {article ? (
                    <article className="blog-article">
                        <header className="blog-heading">
                            <div className="blog-article-tools">
                                <Link className="blog-back-link" to={localePath('/blog', lang)}>
                                    <span aria-hidden="true">←</span>
                                    {t('blog.allArticles')}
                                </Link>
                                <ShareButton key={article.path} />
                            </div>
                            <h1>{article.title}</h1>
                            <p>{article.description}</p>
                            <div className="blog-byline">
                                <Link to={localePath('/about', lang)} className="blog-author">
                                    <BrandMark />
                                    <span>{config.title}</span>
                                </Link>
                                <div className="blog-byline-meta">
                                    <ArticleMeta article={article} />
                                    {article.updatedAt.slice(0, 10) > article.publishedAt.slice(0, 10) && (
                                        <div className="blog-updated">
                                            {t('blog.updated')} <ArticleDate value={article.updatedAt} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </header>
                        {article.cover && (
                            <img
                                className="blog-cover"
                                src={article.cover.url}
                                alt={article.cover.alt}
                                fetchPriority="high"
                            />
                        )}
                        <div className="blog-body" dangerouslySetInnerHTML={{__html: article.bodyHtml}} />
                    </article>
                ) : (
                    <>
                        <header className="blog-index-heading">
                            <h1>
                                {t('footer.blog')}
                                {pageLabel}
                            </h1>
                            <p className="section-subtitle">{description}</p>
                        </header>
                        {content.articles.length === 0 ? (
                            <p>{t('blog.empty')}</p>
                        ) : (
                            <div className="blog-grid">
                                {content.articles.map((item) => (
                                    <BlogCard key={item.path} article={item} />
                                ))}
                            </div>
                        )}
                        {content.totalPages > 1 && (
                            <nav className="blog-pagination" aria-label={t('blog.pagination')}>
                                {content.pagination.previous && (
                                    <Link rel="prev" to={content.pagination.previous}>
                                        {t('blog.previous')}
                                    </Link>
                                )}
                                {content.pagination.pages.map((item, index, pages) => (
                                    <React.Fragment key={item.number}>
                                        {index > 0 && item.number > pages[index - 1].number + 1 && (
                                            <span aria-hidden="true">…</span>
                                        )}
                                        <Link
                                            to={item.path}
                                            aria-current={item.number === content.page ? 'page' : undefined}
                                            aria-label={t('blog.page', {page: item.number})}
                                        >
                                            {item.number}
                                        </Link>
                                    </React.Fragment>
                                ))}
                                {content.pagination.next && (
                                    <Link rel="next" to={content.pagination.next}>
                                        {t('blog.next')}
                                    </Link>
                                )}
                            </nav>
                        )}
                    </>
                )}
                {article && (
                    <>
                        <aside className="blog-generator-invitation" aria-labelledby="blog-generator-title">
                            <div>
                                <h2 id="blog-generator-title">{t('blog.generatorTitle')}</h2>
                                <p>{t('blog.generatorDescription')}</p>
                            </div>
                            <Link className="design-button" to={`${localePath('/', lang)}#generator`}>
                                {t('buttons.create')}
                                <span aria-hidden="true">↗</span>
                            </Link>
                        </aside>
                        <RelatedArticles articles={content.related} />
                    </>
                )}
            </main>
            <Footer />
        </>
    );
}
